import React, {useEffect, useRef} from "react";
import * as d3 from "d3";
import './BubbleMap.scss';
import type {BubbleData} from "./BubbleMapClasses";
import objectHash from "object-hash";
import {observer} from "mobx-react-lite";
import type {D3S} from "../../../utils/global";
import type {FeatureCollection} from "geojson";

const width = 730;
const height = 435;
const MAX_SIZE = 30; // Maximum radius of the bubbles
const MAX_ZOOM = 10;
const AUTOFIT_SCALE_FACTOR: number | false = 0.75; // Factor to ensure bubbles fit nicely in the view

/**
 * This tolerance is needed to prevent the map from being zoomed out at all
 * Zoom between 1 and 1 + TOLERANCE will be reset to 1, to prevent unnecessary zooming
 */
const MAX_ZOOM_OUT_TOLERANCE = 0.1;

export const BubbleMap: React.FC<{
    data: BubbleData[],
    selectedIds?: string[],
    onSelectedChanged?: (selectedIds: string[]) => void,
}> = observer(({data, selectedIds, onSelectedChanged}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!svgRef.current) {
            return;
        }
        const datahash = objectHash(data);
        if (svgRef.current.dataset['datahash'] === datahash && svgRef.current.dataset['selected'] === String(selectedIds)) {
            // Don't re-render if the input data is already rendered
            return;
        }
        svgRef.current.dataset['datahash'] = datahash;
        svgRef.current.dataset['selected'] = String(selectedIds);

        const onClickBg = () => onSelectedChanged ? onSelectedChanged([]) : null;

        function onClickBubble(this, event, d: BubbleData) {
            if (!onSelectedChanged) return;

            let newSelectedIds: string[];
            if (!selectedIds) {
                newSelectedIds = [d.id];
            } else if (!selectedIds.includes(d.id)) {
                newSelectedIds = [...selectedIds, d.id];
            } else {
                newSelectedIds = selectedIds.filter(id => id !== d.id);
            }
            onSelectedChanged(newSelectedIds);
        }

        const svg = d3.select(svgRef.current);
        const tooltip = d3.select(tooltipRef.current);

        svg.html("");
        svg.attr('width', width).attr('height', height);

        // svg > .viewport
        const viewport = svg.append('g')
            .attr('class', 'viewport')

        // svg > .viewport > .background
        viewport.append('rect')
            .attr("class", "background")
            .attr("width", "100%")
            .attr("height", height)
            .on("click", onClickBg)

        // svg > .viewport > .map
        const map = viewport.append('g')
            .attr('class', 'map')

        // svg > .viewport > .bubbles
        const bubbles = viewport.append("g")
            .attr("class", "bubbles")

        // svg > .overlay
        const overlay = svg.append('g')
            .attr('class', 'overlay')

        const zoomBar = overlay.append('rect')
            .attr('class', 'zoom-progress-bar')
            .attr('x', 0)
            .attr('y', height - 5)
            .attr('width', 0)
            .attr('height', 5)


        const projection = d3
            .geoMercator()
            .center([20, 20])
            .scale(100)
            .translate([width / 2, height / 2]);

        d3.json<FeatureCollection>("https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson")
            .then(dataGeo => {
                if (!dataGeo) return;
                // Draw the Map
                map.selectAll()
                    .data(dataGeo.features)
                    .enter()
                    .append('path')
                    .attr("d", feature => d3.geoPath(projection)(feature))
            });

        // Find min and max
        const extentResult: [number, number] | [undefined, undefined] = d3.extent(data, (d) => d.size);
        const extremes: [number, number] = extentResult.some(x => x === undefined)
            ? [0, 0] : extentResult as [number, number];
        const extentX = d3.extent(data.filter(d => d.point), (d) => projection(d.point!)?.at(0) ?? 0);
        const extentY = d3.extent(data.filter(d => d.point), (d) => projection(d.point!)?.at(1) ?? 0);
        const extendArea = extentX[0] && extentX[1] && extentY[0] && extentY[1] ? {
            x: extentX[0], y: extentY[0], width: extentX[1] - extentX[0], height: extentY[1] - extentY[0],
        } : null;

        // Create scale
        const radiusScale = d3.scaleSqrt().domain(extremes).range([
            3, // min
            MAX_SIZE, // max
        ]);
        const color = d3.scaleLinear<string>().domain(extremes).range([
            '#193150',
            '#E62D15',
        ]);

        tooltip.style('visibility', 'hidden');

        function mouseover(this, event, d: BubbleData) {
            tooltip.style('visibility', 'visible');
            d3.select(this).attr('fill-opacity', 0.8);
        }

        function mousemove(this, event, d: BubbleData) {
            tooltip.text(d.tooltip)
                .style("left", event.pageX + "px")
                .style("top", (event.pageY + 20) + "px");
        }

        function mouseleave(this, event, d: BubbleData) {
            tooltip.style('visibility', 'hidden');
            d3.select(this).attr('fill-opacity', selectedIds?.includes(d.id) ? 1 : 0.2);
        }

        const drawBubbles: (circle: D3S<SVGCircleElement, BubbleData>) => void = circle => {
            circle.attr("r", d => radiusScale(d.size))
                .attr("fill", d => d.color ?? color(d.size))
                .attr("fill-opacity", d => selectedIds?.includes(d.id) ? 1 : 0.2)
                .attr("style", () => onSelectedChanged ? 'cursor: pointer' : '')
                .on("mouseover", mouseover)
                .on("mousemove", mousemove)
                .on("mouseleave", mouseleave)
                .on("click", onClickBubble)
        }

        const bubbleCircle = bubbles.selectAll("circle")
            .data(data.filter(d => d.point))
            .enter()
            .append("circle")
            .attr("cx", (d) => {
                const coordinates: [number, number] | null = projection(d.point!)
                return coordinates ? coordinates[0] : 0;
            })
            .attr("cy", (d) => {
                const coordinates: [number, number] | null = projection(d.point!)
                return coordinates ? coordinates[1] : 0;
            })
            .call(drawBubbles);

        const overlayCircle = overlay.selectAll('circle')
            .data(data.filter(d => !d.point))
            .enter()
            .append('circle')
            .attr("cx", d => 1.25 * radiusScale(d.size))
            .attr("cy", d => height - 5 - 1.25 * radiusScale(d.size))
            .call(drawBubbles);

        const zoom = d3.zoom<SVGSVGElement, unknown>()
            .scaleExtent([1, MAX_ZOOM]) // Maximal zoom of 10x
            .translateExtent([[0, 0], [width, height]])
            .extent([[0, 0], [width, height]])
            .on('zoom', event => {
                // Zoom the viewport
                viewport.attr('transform', event.transform);

                // Update the bubble sizes
                bubbleCircle.attr("r", d => radiusScale(d.size) / event.transform.k)

                const k2 = Math.pow(event.transform.k, 1 / 3); // Magic number
                overlayCircle.attr("r", d => radiusScale(d.size) / k2)

                // Update the zoom-progress-bar position
                zoomBar.attr('width', width * ((event.transform.k - 1) / (MAX_ZOOM - 1)))
            })
        svg.call(zoom);

        const boundElement = viewport.node()?.getBBox();

        if (AUTOFIT_SCALE_FACTOR && boundElement && extendArea) {
            const boundWidth = boundElement.width;
            const boundHeight = boundElement.height;

            let scale = Math.min(
                MAX_ZOOM,
                AUTOFIT_SCALE_FACTOR / Math.max(extendArea.width / boundWidth, extendArea.height / boundHeight),
            );
            scale = Math.min(
                MAX_ZOOM,
                AUTOFIT_SCALE_FACTOR / Math.max(
                    (extendArea.width + 2 * MAX_SIZE / scale) / boundWidth,
                    (extendArea.height + 2 * MAX_SIZE / scale) / boundHeight,
                ),
            );
            if (scale <= 1 + MAX_ZOOM_OUT_TOLERANCE) {
                scale = 1;
            }

            const midX = extendArea.x + extendArea.width / 2;
            const midY = extendArea.y + extendArea.height / 2;
            const transform = d3.zoomIdentity
                .translate(boundWidth / 2 - scale * midX, boundHeight / 2 - scale * midY)
                .scale(scale);
            zoom.transform(svg, transform);
        }

        return () => {
            svg.call(zoom.transform, d3.zoomIdentity);
        }
    }, [data, selectedIds, onSelectedChanged]);

    return <div className="bubble-map-container">
        <svg
            className="bubble-map"
            ref={svgRef}
            viewBox={`0 0 ${width} ${height}`}/>
        <div className="tooltip" ref={tooltipRef}/>
    </div>;
});
